import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import { smartAnswersPageSchema } from '../components/StructuredData';
import './Features.css';

const SmartAnswers: React.FC = () => {
  return (
    <div className="feature-page">
      <SEO
        title="Smart Answers - TableChat | Instant Menu & Info for Restaurant Guests"
        description="TableChat answers guest questions about your menu, drinks, hours, allergens, and more — instantly, without involving your staff. Your restaurant's knowledge, available to every guest, every shift."
        canonical="https://tablechat.io/features/smart-answers"
      />
      <StructuredData data={smartAnswersPageSchema} />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Features', url: 'https://tablechat.io/features/smart-answers' },
        { name: 'Smart Answers', url: 'https://tablechat.io/features/smart-answers' },
      ])} />

      {/* Hero */}
      <section className="feature-hero feature-hero-split">
        <div className="container">
          <div className="feature-hero-grid">
            <div className="feature-hero-text">
              <h1>Your Staff Handles Hospitality. TableChat Handles the Questions.</h1>
              <p className="hero-lead">
                "What beers do you have?" "Is anything gluten-free?" "What time do you close?" 
                Your servers answer these questions dozens of times a shift. With TableChat, guests 
                get instant, accurate answers — and your staff stays focused on service, 
                not being a walking FAQ.
              </p>
            </div>
            <div className="feature-hero-image">
              <img 
                src="/smart-answers-hero.png" 
                alt="Guest using TableChat on their phone to ask about beers on tap at a restaurant" 
                className="feature-hero-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem-Solution Blocks */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="section-intro">
            <h2>Every Question Answered, Every Time</h2>
            <p>
              TableChat knows your restaurant — your menu, your drinks, your hours, your details. 
              When a guest asks a question, they get an instant, accurate response with the same 
              knowledge as your most experienced server.
            </p>
          </div>

          <div className="feature-blocks">
            {/* What It Knows */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Answers Specific to Your Restaurant</h3>
                <p>
                  TableChat doesn't give generic responses. When a guest asks "what IPAs do you have?", 
                  they get your actual IPA list. When they ask about allergens, they get answers based on 
                  your actual menu. Every response reflects your restaurant — not a template.
                </p>
                <p>
                  TableChat becomes an extension of your team that never forgets a menu item, never 
                  gives the wrong hours, and is available at every table, every shift.
                </p>
              </div>
              <div className="feature-visual">
                <h4>What TableChat Knows</h4>
                <ul>
                  <li>Your full food menu with descriptions and pricing</li>
                  <li>Drink selections — cocktails, beer, wine, non-alcoholic</li>
                  <li>Desserts and daily specials</li>
                  <li>Allergen and dietary information</li>
                  <li>Business hours, location, and contact details</li>
                  <li>Anything else you want guests to know</li>
                </ul>
              </div>
            </div>

            {/* Staff Efficiency */}
            <div className="feature-block reverse">
              <div className="feature-block-content">
                <h3>Free Your Staff from Repetitive Questions</h3>
                <p>
                  Every time a server walks to a table to answer "what's in the house margarita?" or 
                  "do you have a kids menu?", that's time away from taking orders, running food, and 
                  delivering the kind of attentive service that earns tips and repeat visits.
                </p>
                <p>
                  TableChat handles the informational questions so your servers can focus on what humans 
                  do best — reading the room, making recommendations with personality, and creating 
                  memorable experiences. TableChat handles the facts; your team handles the hospitality.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Questions Guests Ask TableChat</h4>
                <ul>
                  <li>"What beers do you have on tap?"</li>
                  <li>"Is anything on the menu gluten-free?"</li>
                  <li>"What time does the kitchen close?"</li>
                  <li>"Do you have vegetarian options?"</li>
                  <li>"What's in the chef's special?"</li>
                  <li>"Do you take reservations for large groups?"</li>
                </ul>
              </div>
            </div>

            {/* Easy to Manage */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Always Up to Date</h3>
                <p>
                  Seasonal menu change? New cocktail list? Updated hours for the holidays? Update your 
                  information in the management console and TableChat reflects the changes across every 
                  table within minutes. No reprinting, no retraining, no developer needed.
                </p>
                <p>
                  Your restaurant's information stays current with minimal effort — and every guest 
                  conversation from that point forward uses the latest details.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Easy to Manage</h4>
                <ul>
                  <li>Update your information from the management console</li>
                  <li>Changes go live within minutes</li>
                  <li>No technical skills required</li>
                  <li>Restaurant details auto-populated</li>
                  <li>Works from the moment you set it up</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="feature-stats">
        <div className="container">
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-number">Instant</div>
              <p className="stat-label">Guests get answers in seconds — no waiting for a server</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">24/7</div>
              <p className="stat-label">TableChat knows your menu as well as your best server, every shift</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">0</div>
              <p className="stat-label">Server trips to the table for common informational questions</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="feature-how-it-works">
        <div className="container">
          <h2>How Smart Answers Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h4>Set Up Your Restaurant</h4>
              <p>Add your restaurant's information to the management console. Quick, simple, and done once.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h4>Guests Ask Questions</h4>
              <p>When guests tap in at the table and chat, they can ask anything about your restaurant and get instant, accurate answers.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h4>Staff Stays Focused</h4>
              <p>Common informational questions are handled by TableChat. Your servers spend more time on hospitality and less time reciting the menu.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-cta">
        <div className="container">
          <h2>Stop Answering the Same Questions a Hundred Times a Day</h2>
          <p>Let TableChat handle the details so your team can handle the experience.</p>
          <Link to="/signup" className="btn btn-primary btn-large">Get Started Today</Link>
        </div>
      </section>
    </div>
  );
};

export default SmartAnswers;
